import React, { useState, useEffect, useCallback, useRef } from 'react';
import ChatList from './components/ChatList';
import ChatWindow from './components/ChatWindow';
import Login from './pages/Login';
import ApiService from './services/ApiService';
import SignalRService from './services/SignalRService';
import './styles/App.css';
import './styles/PcNewApp.css';

const FRONTEND_VERSION = (process.env.REACT_APP_FRONT_VERSION || '1.4.8').trim();
const NOTIFICATION_PERMISSION_PROMPT_KEY = 'max_notify_permission_prompted_v1';
const MESSAGE_PAGE_SIZE = 50;
const SHOW_TELEGRAM_SEND_UI = false;

function PcNewApp() {
  const isMiniAppContext = typeof window !== 'undefined' && window.location.pathname.startsWith('/miniapp/');
  const MIN_CHAT_LIST_WIDTH = 280;
  const MAX_CHAT_LIST_WIDTH = 680;
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [chatListWidth, setChatListWidth] = useState(() => {
    const raw = localStorage.getItem('chat_list_width_px');
    const parsed = Number.parseInt(raw || '', 10);
    return Number.isFinite(parsed) ? Math.min(MAX_CHAT_LIST_WIDTH, Math.max(MIN_CHAT_LIST_WIDTH, parsed)) : 380;
  });
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoadingOlder, setMessagesLoadingOlder] = useState(false);
  const [backendOffline, setBackendOffline] = useState(false);
  const [reconnectInSec, setReconnectInSec] = useState(10);
  const [lastConnectionError, setLastConnectionError] = useState('');
  const [reconnectInProgress, setReconnectInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frontendUpdateRequired, setFrontendUpdateRequired] = useState(false);
  const [latestFrontendVersion, setLatestFrontendVersion] = useState('');
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSuccess, setSmsSuccess] = useState('');
  const [tgModalOpen, setTgModalOpen] = useState(false);
  const [tgUsers, setTgUsers] = useState([]);
  const [tgSelectedUserId, setTgSelectedUserId] = useState('');
  const [tgText, setTgText] = useState('');
  const [tgSending, setTgSending] = useState(false);
  const [tgError, setTgError] = useState('');
  const [tgSuccess, setTgSuccess] = useState('');
  const [mpModalOpen, setMpModalOpen] = useState(false);
  const [mpPassword, setMpPassword] = useState('');
  const [mpText, setMpText] = useState('');
  const [mpSending, setMpSending] = useState(false);
  const [mpError, setMpError] = useState('');
  const [mpSuccess, setMpSuccess] = useState('');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteChat, setInviteChat] = useState(null);
  const [inviteCandidates, setInviteCandidates] = useState([]);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveChat, setLeaveChat] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [joinRequestQueue, setJoinRequestQueue] = useState([]);
  const [joinRequestPrompt, setJoinRequestPrompt] = useState(null);
  const [joinRequestLoading, setJoinRequestLoading] = useState(false);
  const [joinRequestError, setJoinRequestError] = useState('');
  const [onlineOperators, setOnlineOperators] = useState([]);
  const [unreadTitleCount, setUnreadTitleCount] = useState(0);
  const audioInitializedRef = useRef(false);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pendingNotificationSoundRef = useRef(false);
  const htmlAudioRef = useRef(null);
  const htmlAudioUnlockedRef = useRef(false);
  const toastTimersRef = useRef(new Map());
  const chatsRef = useRef([]);
  const selectedChatRef = useRef(null);
  const loadChatsRef = useRef(null);
  const markChatAsReadRef = useRef(null);
  const messagesPaginationRef = useRef(new Map());
  const lastReadEventRef = useRef({ key: '', at: 0 });
  const markReadInFlightRef = useRef(new Map());
  const forcedReadRef = useRef(new Map());
  const resizeStateRef = useRef({ active: false });
  const updateSoundPlayedRef = useRef(false);
  const originalTitleRef = useRef('');
  const titleBlinkTimerRef = useRef(null);
  const lastIncomingToastRef = useRef({ key: '', at: 0 });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!originalTitleRef.current) {
      originalTitleRef.current = document.title || 'KAT-Telecom MAX Messenger';
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const originalTitle = originalTitleRef.current || document.title || 'KAT-Telecom MAX Messenger';

    if (titleBlinkTimerRef.current) {
      clearInterval(titleBlinkTimerRef.current);
      titleBlinkTimerRef.current = null;
    }

    if (unreadTitleCount <= 0) {
      document.title = originalTitle;
      return undefined;
    }

    let flip = false;
    titleBlinkTimerRef.current = setInterval(() => {
      const alertTitle = '(' + unreadTitleCount + ') Новые сообщения';
      document.title = flip ? originalTitle : alertTitle;
      flip = !flip;
    }, 900);

    return () => {
      if (titleBlinkTimerRef.current) {
        clearInterval(titleBlinkTimerRef.current);
        titleBlinkTimerRef.current = null;
      }
    };
  }, [unreadTitleCount]);

  useEffect(() => {
    const resetUnreadTitle = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        setUnreadTitleCount(0);
      }
    };

    document.addEventListener('visibilitychange', resetUnreadTitle);
    window.addEventListener('focus', resetUnreadTitle);

    return () => {
      document.removeEventListener('visibilitychange', resetUnreadTitle);
      window.removeEventListener('focus', resetUnreadTitle);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('chat_list_width_px', String(chatListWidth));
  }, [chatListWidth]);

  useEffect(() => {
    const onPointerMove = (e) => {
      if (!resizeStateRef.current.active) return;
      const clientX = typeof e.clientX === 'number' ? e.clientX : 0;
      const viewportWidth = window.innerWidth || 0;
      const headerReserve = viewportWidth > 900 ? 420 : 260;
      const dynamicMax = Math.max(MIN_CHAT_LIST_WIDTH, Math.min(MAX_CHAT_LIST_WIDTH, viewportWidth - headerReserve));
      const next = Math.max(MIN_CHAT_LIST_WIDTH, Math.min(dynamicMax, clientX));
      setChatListWidth(next);
      document.body.classList.add('is-resizing-chat-list');
    };

    const stopResize = () => {
      if (!resizeStateRef.current.active) return;
      resizeStateRef.current.active = false;
      document.body.classList.remove('is-resizing-chat-list');
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
      document.body.classList.remove('is-resizing-chat-list');
    };
  }, []);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    if (!isMiniAppContext) return;
    // In mini app mode never auto-open a dialog on startup.
    setSelectedChat(null);
  }, [isMiniAppContext]);

  useEffect(() => {
    return () => {
      toastTimersRef.current.forEach((timer) => clearTimeout(timer));
      toastTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const unlockAudio = async () => {
      audioUnlockedRef.current = true;
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current?.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        audioInitializedRef.current = true;
      } catch {
        // ignore unlock errors
      }

      try {
        if (!htmlAudioRef.current) {
          htmlAudioRef.current = new Audio(createNotificationToneDataUrl());
          htmlAudioRef.current.preload = 'auto';
        }
        const unlockNode = htmlAudioRef.current.cloneNode();
        unlockNode.volume = 0;
        unlockNode.play()
          .then(() => {
            unlockNode.pause();
            unlockNode.currentTime = 0;
          })
          .catch(() => {});
        htmlAudioUnlockedRef.current = true;
      } catch {
        // ignore html audio init errors
      }

      if (pendingNotificationSoundRef.current) {
        pendingNotificationSoundRef.current = false;
        // replay one deferred incoming notification after first user gesture
        setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          playNotificationSound();
        }, 0);
      }
    };

    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((event) => {
      document.addEventListener(event, unlockAudio, { once: true, passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, unlockAudio);
      });
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const ensureAudioContext = useCallback(async () => {
    try {
      if (!audioUnlockedRef.current) {
        return null;
      }
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;
      if (audioContext?.state === 'suspended') {
        await audioContext.resume();
      }
      if (!audioContext || audioContext.state !== 'running') {
        return null;
      }
      audioInitializedRef.current = true;
      return audioContext;
    } catch {
      return null;
    }
  }, []);

  const playNotificationSound = useCallback(async () => {
    if (!audioUnlockedRef.current) {
      pendingNotificationSoundRef.current = true;
      return;
    }

    const audioContext = await ensureAudioContext();
    let played = false;

    try {
      if (audioContext) {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        const now = audioContext.currentTime;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.55, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        oscillator.start(now);
        oscillator.stop(now + 0.1);

        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        oscillator2.frequency.value = 600;
        oscillator2.type = 'sine';
        gainNode2.gain.setValueAtTime(0, now + 0.15);
        gainNode2.gain.linearRampToValueAtTime(0.55, now + 0.16);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        oscillator2.start(now + 0.15);
        oscillator2.stop(now + 0.25);
        played = true;
      }
    } catch {
      // ignore audio playback errors
    }

    if (!played || document.hidden) {
      try {
        if (!htmlAudioRef.current) {
          htmlAudioRef.current = new Audio(createNotificationToneDataUrl());
          htmlAudioRef.current.preload = 'auto';
        }
        if (htmlAudioUnlockedRef.current) {
          const node = htmlAudioRef.current.cloneNode();
          node.volume = 1;
          node.play().catch(() => {});
        }
      } catch {
        // ignore html audio fallback errors
      }
    }
  }, [ensureAudioContext]);

  const normalizeChatId = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const markBackendOnline = useCallback(() => {
    setBackendOffline(false);
    setReconnectInSec(10);
    setLastConnectionError('');
  }, []);

  const isIncomingSignalEvent = useCallback((payload) => {
    const direct = payload?.isFromBot ?? payload?.is_from_bot;
    if (typeof direct === 'boolean') return direct;
    const nested = payload?.message?.isFromBot ?? payload?.message?.is_from_bot;
    if (typeof nested === 'boolean') return nested;
    // Fallback: treat message events as incoming if sender/user id is present.
    return Boolean(payload?.message?.userId ?? payload?.userId ?? payload?.sender?.user_id);
  }, []);

  const markBackendOffline = useCallback((err) => {
    setBackendOffline(true);
    if (err) {
      const message = normalizeConnectionError(err);
      setLastConnectionError(message);
    }
  }, []);

  const loadMessages = useCallback(async (chatId, options = {}) => {
    if (!chatId) return [];
    const normalizedChatId = normalizeChatId(chatId);
    const { appendOlder = false, limit = MESSAGE_PAGE_SIZE, offset = 0 } = options;

    try {
      const data = await SignalRService.getMessages(chatId, limit, offset);
      const list = Array.isArray(data) ? data : [];

      setMessages((prev) => {
        if (!appendOlder) {
          return list;
        }

        // Older page is prepended; keep unique messages by messageId/id.
        const seen = new Set();
        const merged = [...list, ...prev].filter((item) => {
          const key = String(item?.messageId ?? item?.id ?? `${item?.timestamp ?? ''}:${item?.text ?? ''}`);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        return merged;
      });

      const current = messagesPaginationRef.current.get(normalizedChatId) || { offset: 0, hasMore: true };
      const nextOffset = appendOlder ? offset : 0;
      const hasMore = list.length >= limit;
      messagesPaginationRef.current.set(normalizedChatId, {
        offset: nextOffset,
        hasMore
      });
      setMessagesHasMore(hasMore);

      markBackendOnline();
      return list;
    } catch (err) {
      console.error('Message load error:', err);
      markBackendOffline(err);
      return [];
    }
  }, [markBackendOffline, markBackendOnline]);

  const loadOlderMessages = useCallback(async () => {
    const chatId = selectedChatRef.current?.chatId;
    if (!chatId || messagesLoadingOlder) return;

    const normalizedChatId = normalizeChatId(chatId);
    const state = messagesPaginationRef.current.get(normalizedChatId) || { offset: 0, hasMore: true };
    if (!state.hasMore) return;

    try {
      setMessagesLoadingOlder(true);
      const nextOffset = Number(state.offset || 0) + MESSAGE_PAGE_SIZE;
      const page = await loadMessages(chatId, {
        appendOlder: true,
        limit: MESSAGE_PAGE_SIZE,
        offset: nextOffset
      });

      const hasMore = Array.isArray(page) && page.length >= MESSAGE_PAGE_SIZE;
      messagesPaginationRef.current.set(normalizedChatId, {
        offset: nextOffset,
        hasMore
      });
      setMessagesHasMore(hasMore);
    } finally {
      setMessagesLoadingOlder(false);
    }
  }, [loadMessages, messagesLoadingOlder]);

  const showBrowserNotification = useCallback(async (title, body, chatId) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    let permission = Notification.permission;
    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch {
        return;
      }
    }
    if (permission !== 'granted') return;

    try {
      const n = new Notification(title, {
        body,
        tag: `chat-${chatId}-${Date.now()}`,
        renotify: false,
        silent: false,
        timestamp: Date.now()
      });
      n.onclick = () => {
        window.focus();
        const openFromNotification = async () => {
          const chatIdNorm = normalizeChatId(chatId);
          if (!chatIdNorm) return;

          let targetChat = (chatsRef.current || []).find(
            (item) => normalizeChatId(item?.chatId) === chatIdNorm
          );

          if (!targetChat && typeof loadChatsRef.current === 'function') {
            const updated = await loadChatsRef.current();
            targetChat = (updated || []).find(
              (item) => normalizeChatId(item?.chatId) === chatIdNorm
            );
          }

          if (!targetChat) return;

          setSelectedChat(targetChat);
          if (typeof markChatAsReadRef.current === 'function') {
            markChatAsReadRef.current(targetChat.chatId).catch(() => {});
          }
        };

        openFromNotification().catch(() => {});
      };
    } catch {
      // ignore notification errors
    }
  }, []);

  const requestNotificationPermission = useCallback(async (force = false) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;

    try {
      const alreadyPrompted = localStorage.getItem(NOTIFICATION_PERMISSION_PROMPT_KEY) === '1';
      if (alreadyPrompted && !force) {
        return false;
      }

      localStorage.setItem(NOTIFICATION_PERMISSION_PROMPT_KEY, '1');
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch {
      return false;
    }
  }, []);

  const showIncomingToast = useCallback((chatId, fallbackText = 'Новое сообщение') => {
    const chat = (chatsRef.current || []).find((item) => item.chatId === chatId);
    const title = chat
      ? `${chat.firstName || ''} ${chat.lastName || ''}`.trim() || 'Пользователь'
      : 'Новое сообщение';
    const eventText = stripHtmlToText(fallbackText);
    const chatText = stripHtmlToText(chat?.lastMessage);
    const body = (eventText || chatText || 'Новое сообщение').slice(0, 120);
    const dedupeKey = normalizeChatId(chatId);
    const nowMs = Date.now();
    if (lastIncomingToastRef.current.key === dedupeKey && nowMs - lastIncomingToastRef.current.at < 1500) {
      return false;
    }
    lastIncomingToastRef.current = { key: dedupeKey, at: nowMs };
    const id = `${chatId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const toast = {
      id,
      chatId,
      title,
      body
    };

    setToasts((prev) => [toast, ...prev].slice(0, 5));

    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== id));
      toastTimersRef.current.delete(id);
    }, 9000);
    toastTimersRef.current.set(id, timer);

    showBrowserNotification(title, body, chatId).catch(() => {});
    if (typeof document !== 'undefined' && document.hidden) {
      setUnreadTitleCount((prev) => prev + 1);
    }
    return true;
  }, [showBrowserNotification]);

  const loadChats = useCallback(async () => {
    try {
      const data = await SignalRService.getChats();
      const normalizedData = (data || []).map((chat) => {
        const chatIdNorm = normalizeChatId(chat?.chatId);
        const forcedReadAt = forcedReadRef.current.get(chatIdNorm);
        if (!forcedReadAt) {
          return chat;
        }

        const lastMessageTimeMs = chat?.lastMessageTime
          ? new Date(chat.lastMessageTime).getTime()
          : 0;

        // Keep unread at 0 until a newer message appears.
        if (lastMessageTimeMs > forcedReadAt) {
          forcedReadRef.current.delete(chatIdNorm);
          return chat;
        }

        return { ...chat, unreadCount: 0, lastReadAtMs: Math.max(Number(chat?.lastReadAtMs || 0), forcedReadAt) };
      });

      setChats(normalizedData);

      setSelectedChat((prev) => {
        if (prev?.chatId) {
          const selectedNorm = normalizeChatId(prev.chatId);
          const refreshedSelected = normalizedData.find((item) => normalizeChatId(item.chatId) === selectedNorm);
          if (!refreshedSelected) return prev;

          const prevUnread = Number(prev.unreadCount || 0);
          const nextUnread = Number(refreshedSelected.unreadCount || 0);
          const prevLastRead = Number(prev.lastReadAtMs || 0);
          const nextLastRead = Number(refreshedSelected.lastReadAtMs || 0);
          const prevLastMessageTime = prev.lastMessageTime ? new Date(prev.lastMessageTime).toISOString() : '';
          const nextLastMessageTime = refreshedSelected.lastMessageTime ? new Date(refreshedSelected.lastMessageTime).toISOString() : '';

          if (
            prevUnread === nextUnread
            && prevLastRead === nextLastRead
            && (prev.lastMessage || '') === (refreshedSelected.lastMessage || '')
            && prevLastMessageTime === nextLastMessageTime
          ) {
            return prev;
          }

          return refreshedSelected;
        }
        return prev;
      });
      markBackendOnline();
      return data;
    } catch (err) {
      console.error('Chat load error:', err);
      setError(err.message);
      markBackendOffline(err);
      return [];
    }
  }, [markBackendOffline, markBackendOnline]);

  const markChatAsReadServer = useCallback(async (chatId) => {
    if (!chatId) return;
    if (Boolean(user?.accessAllChats) && Boolean(user?.keepUnreadForAccessAllChats)) return;

    const chatIdNorm = normalizeChatId(chatId);
    if (!chatIdNorm) {
      return;
    }
    if (markReadInFlightRef.current.get(chatIdNorm)) {
      return;
    }

    try {
      markReadInFlightRef.current.set(chatIdNorm, true);
      const now = Date.now();
      forcedReadRef.current.set(chatIdNorm, now);
      await ApiService.markChatAsRead(chatId);

      setChats((prev) => prev.map((item) => (
        normalizeChatId(item.chatId) === chatIdNorm
          ? { ...item, unreadCount: 0, lastReadAtMs: now }
          : item
      )));
      setSelectedChat((prev) => {
        if (!prev || normalizeChatId(prev.chatId) !== chatIdNorm) {
          return prev;
        }
        return { ...prev, unreadCount: 0, lastReadAtMs: now };
      });

      const updated = await loadChats();
      const selectedNorm = normalizeChatId(chatId);
      const refreshedSelected = (updated || []).find((c) => normalizeChatId(c.chatId) === selectedNorm);
      if (refreshedSelected && normalizeChatId(selectedChat?.chatId) === selectedNorm) {
        setSelectedChat(refreshedSelected);
      }
      setTimeout(() => {
        loadChats().catch(() => {});
      }, 350);
    } catch (err) {
      console.warn('Mark read failed:', err);
    } finally {
      markReadInFlightRef.current.delete(chatIdNorm);
    }
  }, [loadChats, selectedChat?.chatId, user?.accessAllChats, user?.keepUnreadForAccessAllChats]);
  useEffect(() => {
    loadChatsRef.current = loadChats;
  }, [loadChats]);

  useEffect(() => {
    markChatAsReadRef.current = markChatAsReadServer;
  }, [markChatAsReadServer]);

  const attemptReconnect = useCallback(async () => {
    if (reconnectInProgress) return false;
    setReconnectInProgress(true);
    try {
      await SignalRService.connect();
      await SignalRService.subscribeToAllChats();
      await loadChats();
      if (selectedChat?.chatId) {
        await loadMessages(selectedChat.chatId);
      }
      markBackendOnline();
      return true;
    } catch (err) {
      markBackendOffline(err);
      return false;
    } finally {
      setReconnectInProgress(false);
    }
  }, [reconnectInProgress, loadChats, loadMessages, markBackendOffline, markBackendOnline, selectedChat?.chatId]);

  useEffect(() => {
    if (!backendOffline) return;
    setToasts([]);
  }, [backendOffline]);

  const checkFrontendVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) return;

      const data = await response.json();
      const remoteVersion = normalizeVersion(data?.version);
      const localVersion = normalizeVersion(FRONTEND_VERSION);

      if (!remoteVersion || !localVersion) return;

      setLatestFrontendVersion(remoteVersion);
      if (remoteVersion !== localVersion) {
        setFrontendUpdateRequired(true);
        setToasts([]);
        if (!updateSoundPlayedRef.current) {
          updateSoundPlayedRef.current = true;
          playNotificationSound().catch(() => {});
        }
        return;
      }

      setFrontendUpdateRequired(false);
      updateSoundPlayedRef.current = false;
    } catch {
      // ignore version check errors
    }
  }, [playNotificationSound]);

  const loadOnlineOperators = useCallback(async () => {
    try {
      const items = await ApiService.getOnlineOperators();
      setOnlineOperators(Array.isArray(items) ? items : []);
    } catch {
      // keep previous state on transient errors
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('user_token');
        const userData = localStorage.getItem('user');

        if (token && userData) {
          try {
            const authData = await ApiService.checkAuth();
            const parsedUser = JSON.parse(userData);
            const freshUser = authData?.user || parsedUser;
            setUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
          } catch {
            localStorage.removeItem('user_token');
            localStorage.removeItem('user');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const enqueueJoinRequests = useCallback((items) => {
    const list = Array.isArray(items) ? items : [items];
    const valid = list
      .filter((x) => x && Number.isFinite(Number(x.requestId)) && Number(x.requestId) > 0)
      .map((x) => ({
        requestId: Number(x.requestId),
        chatId: Number(x.chatId || 0),
        message: String(x.message || '').trim() || `В диалоге ${x.chatId} требуется ответ.`,
        createdAt: x.createdAt || new Date().toISOString()
      }));
    if (valid.length === 0) return;

    setJoinRequestQueue((prev) => {
      const ids = new Set([
        ...prev.map((x) => x.requestId),
        ...(joinRequestPrompt ? [joinRequestPrompt.requestId] : [])
      ]);
      const next = [...prev];
      valid.forEach((item) => {
        if (!ids.has(item.requestId)) {
          ids.add(item.requestId);
          next.push(item);
        }
      });
      return next;
    });
  }, [joinRequestPrompt]);

  useEffect(() => {
    if (!joinRequestPrompt && joinRequestQueue.length > 0) {
      setJoinRequestPrompt(joinRequestQueue[0]);
      setJoinRequestQueue((prev) => prev.slice(1));
      setJoinRequestError('');
    }
  }, [joinRequestPrompt, joinRequestQueue]);

  useEffect(() => {
    if (!user || loading) return;

    let connection = null;
    let onNewMessage = null;
    let onChatUpdated = null;
    let onOnlineOperatorsChanged = null;
    let onJoinDialogRequested = null;
    let onJoinDialogRequestResolved = null;

    const initSignalR = async () => {
      try {
        await SignalRService.connect();
        await SignalRService.subscribeToAllChats();
        await loadChats();

        connection = SignalRService.getConnection();
        onNewMessage = async (data) => {
          const eventChatId =
            data?.chatId
            ?? data?.ChatId
            ?? data?.chatID
            ?? data?.message?.chatId
            ?? data?.message?.ChatId;
          if (!eventChatId) {
            return;
          }

          if (!isIncomingSignalEvent(data)) {
            return;
          }

          const incomingText =
            data?.message?.text
            || data?.text
            || 'Новое сообщение';

          if (showIncomingToast(eventChatId, incomingText)) {
            playNotificationSound().catch(() => {});
          }
        };
        onChatUpdated = async (data) => {
          const eventChatId = data?.chatId ?? data?.ChatId ?? data?.chatID;
          const eventReason = String(data?.reason ?? data?.Reason ?? '').toLowerCase();
          const eventUserId = data?.userId ?? data?.UserId ?? '';
          if (!eventChatId) {
            await loadChats();
            const selectedId = selectedChatRef.current?.chatId;
            if (selectedId) {
              await loadMessages(selectedId);
            }
            return;
          }

          const eventChatIdNorm = normalizeChatId(eventChatId);
          const currentSelectedChatId = selectedChatRef.current?.chatId;

          if (eventReason === 'read') {
            const dedupeKey = `${eventChatIdNorm}:${eventUserId}`;
            const now = Date.now();
            if (lastReadEventRef.current.key === dedupeKey && now - lastReadEventRef.current.at < 700) {
              return;
            }
            lastReadEventRef.current = { key: dedupeKey, at: now };
            forcedReadRef.current.set(eventChatIdNorm, now);

            setChats((prev) => prev.map((item) => (
              normalizeChatId(item.chatId) === eventChatIdNorm
                ? { ...item, unreadCount: 0, lastReadAtMs: now }
                : item
            )));
            setSelectedChat((prev) => {
              if (!prev || normalizeChatId(prev.chatId) !== eventChatIdNorm) {
                return prev;
              }
              return { ...prev, unreadCount: 0, lastReadAtMs: now };
            });

            await loadChats();
            if (eventChatIdNorm === normalizeChatId(currentSelectedChatId)) {
              await loadMessages(currentSelectedChatId);
            }
            return;
          }

          const refreshedChats = await loadChats();
          forcedReadRef.current.delete(eventChatIdNorm);

          // Incoming messages from webhook can arrive as ChatUpdated without reason.
          // Use refreshed chats data to avoid stale preview text in notification.
          if (!eventReason) {
            const refreshedChat = (refreshedChats || []).find(
              (item) => normalizeChatId(item?.chatId) === eventChatIdNorm
            );
            if (!refreshedChat) {
              return;
            }
            const incomingText = stripHtmlToText(
              refreshedChat?.lastMessage
              ?? refreshedChat?.last_message
              ?? ''
            ) || 'Новое сообщение';
            if (showIncomingToast(eventChatId, incomingText)) {
              playNotificationSound().catch(() => {});
            }
          }

          // Fallback refresh for open dialog in case NewMessage event is missed or arrives with another shape.
          if (eventChatIdNorm === normalizeChatId(currentSelectedChatId)) {
            await loadMessages(currentSelectedChatId);
            await markChatAsReadServer(currentSelectedChatId);
          }

        };
        onOnlineOperatorsChanged = async () => {
          await loadOnlineOperators();
        };
        onJoinDialogRequested = (data) => {
          enqueueJoinRequests(data);
        };
        onJoinDialogRequestResolved = (data) => {
          const resolvedChatId = normalizeChatId(data?.chatId);
          if (!resolvedChatId) return;
          setJoinRequestQueue((prev) =>
            prev.filter((item) => normalizeChatId(item?.chatId) !== resolvedChatId)
          );
          setJoinRequestPrompt((prev) =>
            (prev && normalizeChatId(prev.chatId) === resolvedChatId) ? null : prev
          );
          setJoinRequestError('');
        };

        connection?.off('ChatUpdated');
        connection?.on('ChatUpdated', onChatUpdated);
        connection?.off('NewMessage', onNewMessage);
        connection?.on('NewMessage', onNewMessage);
        connection?.off('OnlineOperatorsChanged');
        connection?.on('OnlineOperatorsChanged', onOnlineOperatorsChanged);
        connection?.off('JoinDialogRequested');
        connection?.on('JoinDialogRequested', onJoinDialogRequested);
        connection?.off('JoinDialogRequestResolved');
        connection?.on('JoinDialogRequestResolved', onJoinDialogRequestResolved);
      } catch (err) {
        console.error('SignalR init error:', err);
        markBackendOffline(err);
        loadChats();
      }
    };

    initSignalR();

    return () => {
      if (connection && onChatUpdated) {
        connection.off('ChatUpdated', onChatUpdated);
      }
      if (connection && onNewMessage) {
        connection.off('NewMessage', onNewMessage);
      }
      if (connection && onOnlineOperatorsChanged) {
        connection.off('OnlineOperatorsChanged', onOnlineOperatorsChanged);
      }
      if (connection && onJoinDialogRequested) {
        connection.off('JoinDialogRequested', onJoinDialogRequested);
      }
      if (connection && onJoinDialogRequestResolved) {
        connection.off('JoinDialogRequestResolved', onJoinDialogRequestResolved);
      }
    };
  }, [user, loading, loadChats, loadMessages, playNotificationSound, showIncomingToast, markBackendOffline, markChatAsReadServer, loadOnlineOperators, enqueueJoinRequests, isIncomingSignalEvent]);

  useEffect(() => {
    if (!user || loading) return;

    (async () => {
      try {
        const pending = await ApiService.getPendingJoinRequests();
        enqueueJoinRequests(pending);
      } catch {
        // keep app usable on transient backend errors
      }
    })();
  }, [user, loading, enqueueJoinRequests]);

  useEffect(() => {
    if (!user || loading) return undefined;
    if (typeof window === 'undefined' || !('Notification' in window)) return undefined;
    if (Notification.permission !== 'default') return undefined;

    let disposed = false;
    const askOnUserGesture = () => {
      if (disposed) return;
      requestNotificationPermission(true).catch(() => {});
      cleanup();
    };

    const cleanup = () => {
      document.removeEventListener('click', askOnUserGesture);
      document.removeEventListener('keydown', askOnUserGesture);
      document.removeEventListener('touchstart', askOnUserGesture);
    };

    document.addEventListener('click', askOnUserGesture, { passive: true });
    document.addEventListener('keydown', askOnUserGesture);
    document.addEventListener('touchstart', askOnUserGesture, { passive: true });

    return () => {
      disposed = true;
      cleanup();
    };
  }, [user, loading, requestNotificationPermission]);

  useEffect(() => {
    const selectedChatId = selectedChat?.chatId;
    if (!selectedChatId) {
      setMessagesHasMore(false);
      messagesPaginationRef.current.delete(normalizeChatId(selectedChatRef.current?.chatId));
      return;
    }

    let connection = null;
    let onNewMessage = null;

    const setupChat = async () => {
      await loadMessages(selectedChatId);
      await markChatAsReadServer(selectedChatId);

      try {
        await SignalRService.subscribeToChat(selectedChatId);
        connection = SignalRService.getConnection();

        if (connection) {
          onNewMessage = async (data) => {
            const dataChatIdNorm = normalizeChatId(data?.chatId ?? data?.ChatId ?? data?.chatID);
            const selectedChatIdNorm = normalizeChatId(selectedChatId);
            if (dataChatIdNorm === selectedChatIdNorm) {
              await loadMessages(selectedChatId);
              await markChatAsReadServer(selectedChatId);
            }
          };

          connection.on('NewMessage', onNewMessage);
        }
      } catch (err) {
        console.warn('SignalR chat subscribe failed:', err);
      }
    };

    setupChat();

    return () => {
      if (connection && onNewMessage) {
        connection.off('NewMessage', onNewMessage);
      }
      SignalRService.unsubscribeFromChat(selectedChatId).catch(() => {});
    };
  }, [selectedChat?.chatId, loadMessages, markChatAsReadServer]);

  useEffect(() => {
    const selectedChatId = selectedChat?.chatId;
    if (!selectedChatId) return;

    const unread = Number(selectedChat?.unreadCount || 0);
    if (unread <= 0) return;

    markChatAsReadServer(selectedChatId);
  }, [selectedChat?.chatId, selectedChat?.unreadCount, markChatAsReadServer]);

  useEffect(() => {
    if (!backendOffline || !user || loading) return undefined;

    setReconnectInSec(10);

    const countdownTimer = setInterval(() => {
      setReconnectInSec((prev) => (prev <= 1 ? 10 : prev - 1));
    }, 1000);

    const reconnectTimer = setInterval(async () => {
      await attemptReconnect();
    }, 10000);

    return () => {
      clearInterval(countdownTimer);
      clearInterval(reconnectTimer);
    };
  }, [backendOffline, user, loading, attemptReconnect]);

  useEffect(() => {
    if (!user || loading) return undefined;

    const runHeartbeat = async () => {
      try {
        await ApiService.checkAuth();
        markBackendOnline();
      } catch (err) {
        markBackendOffline(err);
      }
    };

    runHeartbeat();

    const heartbeat = setInterval(async () => {
      await runHeartbeat();
    }, 10000);

    return () => {
      clearInterval(heartbeat);
    };
  }, [user, loading, markBackendOnline, markBackendOffline]);

  useEffect(() => {
    if (loading) return undefined;

    checkFrontendVersion();
    const timer = setInterval(() => {
      checkFrontendVersion();
    }, 15000);

    return () => {
      clearInterval(timer);
    };
  }, [loading, checkFrontendVersion]);

  useEffect(() => {
    if (!user || loading) return undefined;
    loadOnlineOperators();
    const timer = setInterval(() => {
      loadOnlineOperators();
    }, 10000);
    return () => clearInterval(timer);
  }, [user, loading, loadOnlineOperators]);

  useEffect(() => {
    const onEscCloseChat = (e) => {
      if (e.key !== 'Escape') return;
      if (!selectedChatRef.current?.chatId) return;
      setSelectedChat(null);
      setMessages([]);
      setMessagesHasMore(false);
      setToasts([]);
    };

    document.addEventListener('keydown', onEscCloseChat);
    return () => {
      document.removeEventListener('keydown', onEscCloseChat);
    };
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user');
    setUser(null);
    setChats([]);
    setSelectedChat(null);
    setMessages([]);
    setMessagesHasMore(false);
    messagesPaginationRef.current.clear();
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  if (loading) {
    return (
      <div className="pc-new-app app loading">
        <div className="loader">
          <div className="spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    markChatAsReadServer(chat?.chatId);
    setToasts([]);
  };

  const handleSendMessage = async (chatId, text) => {
    try {
      const updatedChats = chats.map((chat) =>
        chat.chatId === chatId
          ? { ...chat, lastMessage: text, lastMessageTime: new Date() }
          : chat
      );
      setChats(updatedChats);
      await loadMessages(chatId);
    } catch (err) {
      console.error('UI update error:', err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!selectedChat?.chatId || !messageId) return;
    try {
      await ApiService.deleteMessage(messageId);
      await loadMessages(selectedChat.chatId);
    } catch (err) {
      console.error('Delete message error:', err);
      setError(err.message);
    }
  };

  const handleEditMessage = async () => {
    if (!selectedChat?.chatId) return;
    try {
      await loadMessages(selectedChat.chatId);
      await loadChats();
    } catch (err) {
      console.error('Refresh after edit error:', err);
    }
  };

  const closeIncomingToast = (toastId) => {
    const timer = toastTimersRef.current.get(toastId);
    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(toastId);
    }
    setToasts((prev) => prev.filter((item) => item.id !== toastId));
  };

  const handleToastClick = async (toast) => {
    if (isMiniAppContext) {
      closeIncomingToast(toast?.id);
      return;
    }

    if (!toast?.chatId) return;

    const existing = chats.find((c) => c.chatId === toast.chatId);
    if (existing) {
      setSelectedChat(existing);
      markChatAsReadServer(existing.chatId);
      closeIncomingToast(toast.id);
      return;
    }

    const updated = await loadChats();
    const found = (updated || []).find((c) => c.chatId === toast.chatId);
    if (found) {
      setSelectedChat(found);
      markChatAsReadServer(found.chatId);
    }
    closeIncomingToast(toast.id);
  };

  const handleReconnectNow = async () => {
    await attemptReconnect();
  };

  const handleDeleteDialogRequest = async (chat) => {
    const chatId = chat?.chatId;
    if (!chatId) return;

    const password = window.prompt('Введите пароль для удаления диалога');
    if (password === null) return;

    try {
      const result = await ApiService.deleteDialog(chatId, password);
      if (!result?.success) {
        throw new Error(result?.message || 'Не удалось удалить диалог');
      }

      if (normalizeChatId(selectedChatRef.current?.chatId) === normalizeChatId(chatId)) {
        setSelectedChat(null);
        setMessages([]);
        setMessagesHasMore(false);
      }

      setToasts([]);
      await loadChats();
    } catch (err) {
      setError(err.message || 'Не удалось удалить диалог');
    }
  };

  const handleMarkChatUnreadRequest = async (chat) => {
    const chatId = chat?.chatId;
    if (!chatId) return;

    try {
      await ApiService.markChatAsUnread(chatId);
      await loadChats();
    } catch (err) {
      setError(err?.message || 'Не удалось отметить диалог как непрочитанный');
    }
  };

  const handleMarkChatReadRequest = async (chat) => {
    const chatId = chat?.chatId;
    if (!chatId) return;

    try {
      await ApiService.markChatAsRead(chatId);
      await loadChats();
    } catch (err) {
      setError(err?.message || 'Не удалось отметить диалог как прочитанный');
    }
  };

  const handleChatListResizeStart = (e) => {
    if (isMiniAppContext) return;
    if (window.innerWidth < 900) return;
    resizeStateRef.current.active = true;
    document.body.classList.add('is-resizing-chat-list');
    e.preventDefault();
  };

  const handleOpenSmsModal = () => {
    setSmsModalOpen(true);
    setSmsError('');
    setSmsSuccess('');
  };

  const handleCloseSmsModal = () => {
    if (smsSending) return;
    setSmsModalOpen(false);
    setSmsError('');
    setSmsSuccess('');
  };

  const handleSmsPhoneChange = (e) => {
    const formatted = formatPhoneForInput(e.target.value);
    setSmsPhone(formatted);
    setSmsError('');
    setSmsSuccess('');
  };

  const handleSmsTextChange = (e) => {
    const next = (e.target.value || '').slice(0, 69);
    setSmsText(next);
    setSmsError('');
    setSmsSuccess('');
  };

  const handleSendSmsFromModal = async () => {
    const digits = normalizePhoneDigits(smsPhone);
    const text = (smsText || '').trim();

    if (digits.length !== 11 || !digits.startsWith('7')) {
      setSmsError('Номер телефона должен быть в формате +7(XXX)XXX-XX-XX');
      return;
    }

    if (!text) {
      setSmsError('Введите текст сообщения');
      return;
    }

    if (text.length > 69) {
      setSmsError('Сообщение должно быть не длиннее 69 символов');
      return;
    }

    try {
      setSmsSending(true);
      setSmsError('');
      const result = await ApiService.sendSmsForward(digits, text);
      setSmsSuccess(result?.message || 'SMS отправлено');
      setSmsPhone(formatPhoneForInput(digits));
      setSmsText('');
    } catch (err) {
      setSmsError(err.message || 'Не удалось отправить SMS');
    } finally {
      setSmsSending(false);
    }
  };

  const handleOpenTgModal = async () => {
    setTgModalOpen(true);
    setTgError('');
    setTgSuccess('');
    setTgText('');
    try {
      const users = await ApiService.getTelegramUsers();
      const list = Array.isArray(users) ? users : [];
      setTgUsers(list);
      setTgSelectedUserId(list[0]?.userId ? String(list[0].userId) : '');
    } catch (err) {
      setTgUsers([]);
      setTgSelectedUserId('');
      setTgError(err.message || 'Не удалось загрузить список пользователей Telegram');
    }
  };

  const handleCloseTgModal = () => {
    if (tgSending) return;
    setTgModalOpen(false);
    setTgError('');
    setTgSuccess('');
  };

  const handleTgTextChange = (e) => {
    const next = (e.target.value || '').slice(0, 69);
    setTgText(next);
    setTgError('');
    setTgSuccess('');
  };

  const handleSendTgFromModal = async () => {
    const text = (tgText || '').trim();
    const userId = Number.parseInt(tgSelectedUserId || '', 10);

    if (!Number.isFinite(userId) || userId <= 0) {
      setTgError('Выберите пользователя');
      return;
    }

    if (!text) {
      setTgError('Введите текст сообщения');
      return;
    }

    if (text.length > 69) {
      setTgError('Сообщение должно быть не длиннее 69 символов');
      return;
    }

    try {
      setTgSending(true);
      setTgError('');
      const result = await ApiService.sendTelegramDirect(userId, text);
      setTgSuccess(result?.message || 'Сообщение в Telegram отправлено');
      setTgText('');
    } catch (err) {
      setTgError(err.message || 'Не удалось отправить сообщение в Telegram');
    } finally {
      setTgSending(false);
    }
  };

  const handleOpenMpModal = () => {
    const password = window.prompt('Введите пароль');
    if (password === null) return;
    setMpPassword(password);
    setMpText('');
    setMpError('');
    setMpSuccess('');
    setMpModalOpen(true);
  };

  const handleCloseMpModal = () => {
    if (mpSending) return;
    setMpModalOpen(false);
    setMpError('');
    setMpSuccess('');
  };

  const handleMpTextChange = (e) => {
    const next = (e.target.value || '').slice(0, 69);
    setMpText(next);
    setMpError('');
    setMpSuccess('');
  };

  const handleSendMpFromModal = async () => {
    const text = (mpText || '').trim();
    if (!text) {
      setMpError('Введите текст сообщения');
      return;
    }
    if (text.length > 69) {
      setMpError('Сообщение должно быть не длиннее 69 символов');
      return;
    }

    try {
      setMpSending(true);
      setMpError('');
      const result = await ApiService.sendMassChatMessage(mpPassword, text);
      setMpSuccess(result?.message || 'Массовая отправка выполнена');
      setMpText('');
    } catch (err) {
      setMpError(err.message || 'Не удалось выполнить массовую отправку');
    } finally {
      setMpSending(false);
    }
  };

  const handleOpenInviteModal = async (chat) => {
    if (!chat?.chatId) return;
    setInviteModalOpen(true);
    setInviteChat(chat);
    setInviteError('');
    setInviteCandidates([]);
    setInviteUserId('');
    try {
      const users = await ApiService.getInviteCandidates(chat.chatId);
      const rawList = Array.isArray(users) ? users : [];
      const onlineOperatorIds = new Set((onlineOperators || []).map((op) => Number(op?.userId)));
      const list = rawList.map((candidate) => {
        const hasBackendOnlineFlag = typeof candidate?.isOnline === 'boolean';
        const fallbackOnline = onlineOperatorIds.has(Number(candidate?.userId));
        return {
          ...candidate,
          isOnline: hasBackendOnlineFlag ? candidate.isOnline : fallbackOnline
        };
      });
      setInviteCandidates(list);
      const firstSelectable = list.find((u) => Boolean(u?.isOnline));
      setInviteUserId(firstSelectable ? String(firstSelectable.userId) : '');
    } catch (err) {
      setInviteError(err.message || 'Не удалось загрузить список пользователей');
    }
  };

  const handleCloseInviteModal = (force = false) => {
    if (inviteLoading && !force) return;
    setInviteModalOpen(false);
    setInviteChat(null);
    setInviteCandidates([]);
    setInviteUserId('');
    setInviteError('');
  };

  const handleInviteUser = async () => {
    if (!inviteChat?.chatId) return;
    const chatId = inviteChat.chatId;
    const canInviteOffline = Boolean(user?.accessAllChats);
    const targetUserId = Number.parseInt(inviteUserId || '', 10);
    if (!Number.isFinite(targetUserId) || targetUserId <= 0) {
      setInviteError('Выберите пользователя');
      return;
    }
    const selectedCandidate = inviteCandidates.find((x) => Number(x?.userId) === targetUserId);
    if (!canInviteOffline && !selectedCandidate?.isOnline) {
      setInviteError('Нельзя пригласить пользователя не в сети');
      return;
    }

    try {
      setInviteLoading(true);
      setInviteError('');
      await ApiService.inviteToDialog(chatId, targetUserId);
      handleCloseInviteModal(true);
      await loadChats();
    } catch (err) {
      setInviteError(err.message || 'Не удалось пригласить пользователя');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleKickUserFromDialog = async (chat, participant) => {
    if (!chat?.chatId || !participant?.userId) return;
    await ApiService.kickFromDialog(chat.chatId, participant.userId);
    await loadChats();
  };

  const handleOpenLeaveModal = (chat) => {
    if (!chat?.chatId) return;
    setLeaveModalOpen(true);
    setLeaveChat(chat);
    setLeaveError('');
  };

  const handleCloseLeaveModal = (force = false) => {
    if (leaveLoading && !force) return;
    setLeaveModalOpen(false);
    setLeaveChat(null);
    setLeaveError('');
  };

  const handleConfirmLeaveDialog = async () => {
    if (!leaveChat?.chatId) return;
    try {
      setLeaveLoading(true);
      setLeaveError('');
      await ApiService.leaveDialog(leaveChat.chatId);
      if (normalizeChatId(selectedChat?.chatId) === normalizeChatId(leaveChat.chatId)) {
        setSelectedChat(null);
        setMessages([]);
        setMessagesHasMore(false);
      }
      await loadChats();
      handleCloseLeaveModal(true);
    } catch (err) {
      setLeaveError(err.message || 'Не удалось выйти из диалога');
    } finally {
      setLeaveLoading(false);
    }
  };

  const handleAcceptJoinRequest = async () => {
    if (!joinRequestPrompt?.requestId) return;
    try {
      setJoinRequestLoading(true);
      setJoinRequestError('');
      const result = await ApiService.acceptJoinRequest(joinRequestPrompt.requestId);
      const joinedChatId = Number(result?.chatId || joinRequestPrompt.chatId || 0);
      const updatedChats = await loadChats();
      const joinedChat = (updatedChats || []).find((x) => normalizeChatId(x.chatId) === normalizeChatId(joinedChatId));
      if (joinedChat) {
        setSelectedChat(joinedChat);
        await loadMessages(joinedChat.chatId);
        await markChatAsReadServer(joinedChat.chatId);
      }
      setJoinRequestPrompt(null);
    } catch (err) {
      setJoinRequestError(err?.message || 'Не удалось подключиться к диалогу');
    } finally {
      setJoinRequestLoading(false);
    }
  };

  const handleRejectJoinRequest = async () => {
    if (!joinRequestPrompt?.requestId) return;
    try {
      setJoinRequestLoading(true);
      setJoinRequestError('');
      await ApiService.rejectJoinRequest(joinRequestPrompt.requestId);
      setJoinRequestPrompt(null);
    } catch (err) {
      setJoinRequestError(err?.message || 'Не удалось отклонить уведомление');
    } finally {
      setJoinRequestLoading(false);
    }
  };
  const handleTestBrowserNotification = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      window.alert('Браузер не поддерживает системные уведомления');
      return;
    }

    const granted = await requestNotificationPermission(true);
    if (!granted) {
      window.alert('Разрешите уведомления для этого сайта в браузере');
      return;
    }

    audioUnlockedRef.current = true;
    htmlAudioUnlockedRef.current = true;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      if (!htmlAudioRef.current) {
        htmlAudioRef.current = new Audio(createNotificationToneDataUrl());
        htmlAudioRef.current.preload = 'auto';
      }
      await playNotificationSound();
    } catch {
      // ignore audio test errors
    }

    try {
      const n = new Notification('Тест уведомления', {
        body: 'KAT-Telecom MAX Messenger: проверка уведомлений',
        tag: `max-test-notification-${Date.now()}`,
        renotify: false,
        silent: false,
        timestamp: Date.now()
      });
      n.onclick = () => {
        window.focus();
      };
    } catch {
      window.alert('Не удалось показать системное уведомление');
    }
  };

  return (
    <div className={`pc-new-app app ${backendOffline ? 'app-offline' : ''} ${frontendUpdateRequired ? 'app-update-required' : ''} ${(smsModalOpen || tgModalOpen || mpModalOpen || inviteModalOpen || leaveModalOpen || !!joinRequestPrompt) ? 'app-sms-modal-open' : ''}`.trim()}>
      <div className="app-header">
        <div className="app-brand">
          <img className="app-brand-logo" src="/kat.png" alt="KAT-Telecom" />
          <div className="app-brand-title-wrap">
            <h1>KAT-Telecom MAX Messenger</h1>
            <span className="app-brand-version">v{FRONTEND_VERSION}</span>
          </div>
        </div>
        <div className="user-info">
          <span className={`backend-status ${backendOffline ? 'offline' : 'online'}`}>
            {backendOffline ? 'Офлайн' : 'Онлайн'}
          </span>
          <span>{user.displayName}</span>
          <button className="theme-btn" onClick={toggleTheme}>
            {theme === 'light' ? '🌙' : '☀'}
          </button>
          <button className="theme-btn" onClick={handleTestBrowserNotification} title="Тест системного уведомления">🔔</button>
          <button className="logout-btn" onClick={handleLogout}>Выйти</button>
        </div>
      </div>
      <div className="container">
        <div className="chat-list-pane" style={{ width: `${chatListWidth}px` }}>
          <ChatList
            chats={chats}
            selectedChat={selectedChat}
            onSelectChat={handleSelectChat}
            onDeleteDialogRequest={handleDeleteDialogRequest}
            onMarkUnreadRequest={handleMarkChatUnreadRequest}
            onMarkReadRequest={handleMarkChatReadRequest}
            canManageReadState={Boolean(user?.accessAllChats)}
            showOperatorFilter={Boolean(user?.accessAllChats)}
          />
        </div>
        {!isMiniAppContext && (
          <div
            className="chat-list-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Изменить ширину списка чатов"
            onPointerDown={handleChatListResizeStart}
          />
        )}
        <ChatWindow
          chat={selectedChat}
          messages={messages}
          hasMoreMessages={messagesHasMore}
          loadingOlderMessages={messagesLoadingOlder}
          onLoadOlderMessages={loadOlderMessages}
          currentUser={user}
          onSendMessage={handleSendMessage}
          onEditMessage={handleEditMessage}
          onDeleteMessage={handleDeleteMessage}
          onInviteToDialog={handleOpenInviteModal}
          onKickFromDialog={handleKickUserFromDialog}
          onLeaveDialog={handleOpenLeaveModal}
          error={error}
          lastReadAt={selectedChat?.lastReadAtMs || 0}
        />
        {!isMiniAppContext && (
          <aside className="future-tools-pane" aria-label="Панель будущих функций">
            <div className="future-tools-title">ФУНКЦИИ</div>
            <button type="button" className="future-tool-btn sms" title="СМС" onClick={handleOpenSmsModal}>СМС</button>
            {SHOW_TELEGRAM_SEND_UI && (
              <button type="button" className="future-tool-btn tg" title="Telegram" onClick={handleOpenTgModal}>TG</button>
            )}
            <button type="button" className="future-tool-btn mp" title="Массовая рассылка" onClick={handleOpenMpModal}>МР</button>
            <button type="button" className="future-tool-btn" title="Скоро" disabled>+</button>
            <button type="button" className="future-tool-btn" title="Скоро" disabled>+</button>
            <div className="future-online-title">ОНЛАЙН</div>
            <div className="future-online-list">
              {onlineOperators.map((op) => (
                <div
                  key={op.userId}
                  className="future-online-avatar"
                  title={`${op.displayName || op.username}${op.connections > 1 ? ` (${op.connections})` : ''}`}
                >
                  {op.avatarUrl ? (
                    <img src={op.avatarUrl} alt={op.displayName || op.username || 'user'} />
                  ) : (
                    <span>{getAvatarInitialsFromLogin(op.username)}</span>
                  )}
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
      {smsModalOpen && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Отправка SMS">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Отправка SMS</div>
            <label className="sms-modal-label" htmlFor="smsPhoneInput">Номер телефона</label>
            <input
              id="smsPhoneInput"
              className="sms-modal-input"
              type="tel"
              inputMode="numeric"
              placeholder="+7(900)123-45-67"
              value={smsPhone}
              onChange={handleSmsPhoneChange}
              disabled={smsSending}
            />
            <label className="sms-modal-label" htmlFor="smsTextInput">Сообщение</label>
            <textarea
              id="smsTextInput"
              className="sms-modal-textarea"
              placeholder="Введите сообщение"
              value={smsText}
              onChange={handleSmsTextChange}
              maxLength={69}
              disabled={smsSending}
            />
            <div className="sms-modal-counter">{smsText.length}/69</div>
            {smsError && <div className="sms-modal-error">{smsError}</div>}
            {smsSuccess && <div className="sms-modal-success">{smsSuccess}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleCloseSmsModal} disabled={smsSending}>Закрыть</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleSendSmsFromModal} disabled={smsSending}>
                {smsSending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {SHOW_TELEGRAM_SEND_UI && tgModalOpen && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Отправка Telegram">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Отправка в Telegram</div>
            <label className="sms-modal-label" htmlFor="tgUserSelect">Пользователь</label>
            <select
              id="tgUserSelect"
              className="sms-modal-select"
              value={tgSelectedUserId}
              onChange={(e) => {
                setTgSelectedUserId(e.target.value);
                setTgError('');
                setTgSuccess('');
              }}
              disabled={tgSending}
            >
              <option value="">Выберите пользователя</option>
              {tgUsers.map((tgUser) => (
                <option key={tgUser.userId} value={tgUser.userId}>
                  {(tgUser.displayName || tgUser.username || `ID ${tgUser.userId}`)} ({tgUser.telegramId})
                </option>
              ))}
            </select>
            <label className="sms-modal-label" htmlFor="tgTextInput">Сообщение</label>
            <textarea
              id="tgTextInput"
              className="sms-modal-textarea"
              placeholder="Введите сообщение"
              value={tgText}
              onChange={handleTgTextChange}
              maxLength={69}
              disabled={tgSending}
            />
            <div className="sms-modal-counter">{tgText.length}/69</div>
            {tgError && <div className="sms-modal-error">{tgError}</div>}
            {tgSuccess && <div className="sms-modal-success">{tgSuccess}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleCloseTgModal} disabled={tgSending}>Закрыть</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleSendTgFromModal} disabled={tgSending || tgUsers.length === 0}>
                {tgSending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {mpModalOpen && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Массовая рассылка">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Массовая рассылка</div>
            <label className="sms-modal-label" htmlFor="mpTextInput">Сообщение</label>
            <textarea
              id="mpTextInput"
              className="sms-modal-textarea"
              placeholder="Введите сообщение"
              value={mpText}
              onChange={handleMpTextChange}
              maxLength={69}
              disabled={mpSending}
            />
            <div className="sms-modal-counter">{mpText.length}/69</div>
            {mpError && <div className="sms-modal-error">{mpError}</div>}
            {mpSuccess && <div className="sms-modal-success">{mpSuccess}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleCloseMpModal} disabled={mpSending}>Закрыть</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleSendMpFromModal} disabled={mpSending}>
                {mpSending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {inviteModalOpen && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Пригласить в диалог">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Пригласить в диалог</div>
            <label className="sms-modal-label" htmlFor="inviteUserSelect">Пользователь</label>
            <select
              id="inviteUserSelect"
              className="sms-modal-select"
              value={inviteUserId}
              onChange={(e) => setInviteUserId(e.target.value)}
              disabled={inviteLoading}
            >
              <option value="">Выберите пользователя</option>
              {inviteCandidates.map((candidate) => (
                <option
                  key={candidate.userId}
                  value={candidate.userId}
                  disabled={!candidate.isOnline && !user?.accessAllChats}
                >
                  {candidate.isOnline ? '🟢 ' : '⚪ '}
                  {(candidate.displayName || candidate.username || `ID ${candidate.userId}`)}
                  {!candidate.isOnline ? ' (не в сети)' : ''}
                  {candidate.isVisibleForUser ? ' (уже видит чат)' : ''}
                </option>
              ))}
            </select>
            {inviteError && <div className="sms-modal-error">{inviteError}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleCloseInviteModal} disabled={inviteLoading}>Закрыть</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleInviteUser} disabled={inviteLoading || !inviteUserId}>
                {inviteLoading ? 'Отправка...' : 'Пригласить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {leaveModalOpen && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Выйти из диалога">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Выйти из диалога</div>
            <div className="sms-modal-label" style={{ marginTop: 0 }}>Вы уверены что хотите покинуть диалог?</div>
            {leaveError && <div className="sms-modal-error">{leaveError}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleCloseLeaveModal} disabled={leaveLoading}>НЕТ</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleConfirmLeaveDialog} disabled={leaveLoading}>
                {leaveLoading ? 'Обработка...' : 'ДА'}
              </button>
            </div>
          </div>
        </div>
      )}
      {joinRequestPrompt && (
        <div className="sms-modal-overlay" role="dialog" aria-modal="true" aria-label="Вход в диалог">
          <div className="sms-modal-card">
            <div className="sms-modal-title">Требуется ответ в диалоге</div>
            <div className="sms-modal-label" style={{ marginTop: 0 }}>{joinRequestPrompt.message}</div>
            {joinRequestError && <div className="sms-modal-error">{joinRequestError}</div>}
            <div className="sms-modal-actions">
              <button type="button" className="sms-modal-btn secondary" onClick={handleRejectJoinRequest} disabled={joinRequestLoading}>ОТМЕНА</button>
              <button type="button" className="sms-modal-btn primary" onClick={handleAcceptJoinRequest} disabled={joinRequestLoading}>
                {joinRequestLoading ? 'Обработка...' : 'ВОЙТИ В ДИАЛОГ'}
              </button>
            </div>
          </div>
        </div>
      )}
      {frontendUpdateRequired && (
        <div className="offline-overlay" role="alert" aria-live="assertive">
          <div className="offline-card">
            <div className="offline-title">Вышло обновление</div>
            <div className="offline-subtitle">Вышло обновление нажмите CTRL+SHFT+R</div>
            <div className="offline-last-error">
              Текущая версия: {FRONTEND_VERSION}
              {latestFrontendVersion ? ` | Новая версия: ${latestFrontendVersion}` : ''}
            </div>
          </div>
        </div>
      )}
      {!frontendUpdateRequired && backendOffline && (
        <div className="offline-overlay" role="alert" aria-live="assertive">
          <div className="offline-card">
            <div className="offline-title">Ошибка подключения</div>
            <div className="offline-subtitle">Автоматическое переподключение через {reconnectInSec} сек</div>
            {lastConnectionError && (
              <div className="offline-last-error">{lastConnectionError}</div>
            )}
            <button
              type="button"
              className="offline-reconnect-btn"
              onClick={handleReconnectNow}
              disabled={reconnectInProgress}
            >
              {reconnectInProgress ? 'Подключение...' : 'Переподключить сейчас'}
            </button>
          </div>
        </div>
      )}
      {toasts.length > 0 && (
        <div className="incoming-toast-stack" aria-live="polite">
          {toasts.map((toast) => (
            <button key={toast.id} className="incoming-toast" onClick={() => handleToastClick(toast)}>
              <div className="incoming-toast-title">{toast.title}</div>
              <div className="incoming-toast-body">{toast.body}</div>
              <span
                className="incoming-toast-close"
                onClick={(e) => {
                  e.stopPropagation();
                  closeIncomingToast(toast.id);
                }}
              >
                ×
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function stripHtmlToText(value) {
  if (!value) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${value}</div>`, 'text/html');
  return (doc.body.textContent || '').replace(/\u00A0/g, ' ').trim();
}

function normalizePhoneDigits(value) {
  if (!value) return '';
  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    digits = `7${digits.slice(1)}`;
  }
  if (digits.length > 11) {
    digits = digits.slice(0, 11);
  }
  return digits;
}

function formatPhoneForInput(value) {
  let digits = normalizePhoneDigits(value);
  if (!digits) return '';
  if (digits[0] !== '7') {
    digits = `7${digits.slice(0, 10)}`;
  }

  const p1 = digits.slice(1, 4);
  const p2 = digits.slice(4, 7);
  const p3 = digits.slice(7, 9);
  const p4 = digits.slice(9, 11);

  let result = '+7';
  if (p1) result += `(${p1}`;
  if (p1.length === 3) result += ')';
  if (p2) result += p2;
  if (p3) result += `-${p3}`;
  if (p4) result += `-${p4}`;
  return result;
}

function getAvatarInitialsFromLogin(login) {
  const cleaned = String(login || '')
    .trim()
    .replace(/[^a-zA-Zа-яА-Я0-9]/g, '');
  if (!cleaned) return '??';
  return cleaned.slice(0, 2).toUpperCase();
}

function normalizeConnectionError(err) {
  const raw = (typeof err === 'string' ? err : (err?.message || '')).trim();
  if (!raw) return 'Сервер временно недоступен';

  const lower = raw.toLowerCase();
  const statusMatch = raw.match(/\b(4\d\d|5\d\d)\b/);
  const status = statusMatch?.[1] || '';

  if (lower.includes('502') || lower.includes('bad gateway')) {
    return 'Сервер недоступен (502 Bad Gateway)';
  }
  if (lower.includes('503') || lower.includes('service unavailable')) {
    return 'Сервис временно недоступен (503 Service Unavailable)';
  }
  if (lower.includes('504') || lower.includes('gateway timeout')) {
    return 'Таймаут соединения с сервером (504 Gateway Timeout)';
  }
  if (lower.includes('401') || lower.includes('unauthorized')) {
    return 'Требуется повторная авторизация (401)';
  }
  if (lower.includes('403') || lower.includes('forbidden')) {
    return 'Доступ запрещён (403)';
  }
  if (lower.includes('failed to fetch') || lower.includes('network') || lower.includes('negotiation')) {
    return status ? `Ошибка подключения к серверу (${status})` : 'Ошибка подключения к серверу';
  }

  // Remove HTML and keep message compact.
  const plain = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plain) return 'Сервер временно недоступен';
  return plain.length > 160 ? `${plain.slice(0, 160)}...` : plain;
}

function normalizeVersion(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
}

function createNotificationToneDataUrl() {
  const sampleRate = 8000;
  const durationSec = 0.2;
  const frameCount = Math.floor(sampleRate * durationSec);
  const bytesPerSample = 2;
  const dataSize = frameCount * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  const frequency = 880;
  const amplitude = 0.45;
  for (let i = 0; i < frameCount; i += 1) {
    const t = i / sampleRate;
    const envelope = Math.max(0, 1 - (i / frameCount));
    const sample = Math.sin(2 * Math.PI * frequency * t) * amplitude * envelope;
    const int16 = Math.max(-1, Math.min(1, sample)) * 0x7fff;
    view.setInt16(44 + i * 2, int16, true);
  }

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:audio/wav;base64,${btoa(binary)}`;
}

export default PcNewApp;











